import React from 'react';
import { BarChart3, Phone, MessageCircle, Mail, Globe, MapPin } from 'lucide-react';

const VendorAnalyticsTab = ({ stall, stallAnalytics, linkClickAnalytics }) => {
  return (
    <div className="bg-gray-50 dark:bg-[#252525] p-6 rounded-xl space-y-4">
      <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-[#FFD12D]" />
        Stall Analytics: {stall?.stallName}
      </h3>

      {stallAnalytics ? (
        <div className="space-y-6">
          {/* Views Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800 text-center">
              <div className="text-3xl font-bold text-blue-600">{stallAnalytics.totalViews || 0}</div>
              <div className="text-sm text-blue-600/80">Total Views</div>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800 text-center">
              <div className="text-3xl font-bold text-green-600">{stallAnalytics.todayViews || 0}</div>
              <div className="text-sm text-green-600/80">Today</div>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800 text-center">
              <div className="text-3xl font-bold text-purple-600">{stallAnalytics.last7DaysViews || 0}</div>
              <div className="text-sm text-purple-600/80">Last 7 Days</div>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800 text-center">
              <div className="text-3xl font-bold text-yellow-600">{stallAnalytics.last30DaysViews || 0}</div>
              <div className="text-sm text-yellow-600/80">Last 30 Days</div>
            </div>
          </div>

          {/* Recent Views */}
          <div>
            <h4 className="font-semibold text-[#252525] dark:text-white mb-3">Recent Views</h4>
            <div className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-y-auto">
              {(stallAnalytics.recentViews || []).length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No views recorded yet</p>
                  <p className="text-sm">Views will appear here when customers view your stall</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {(stallAnalytics.recentViews || []).map((view) => (
                    <div key={view.id} className="p-3 flex justify-between items-center">
                      <span className="text-sm text-[#252525] dark:text-white">
                        Stall viewed
                      </span>
                      <span className="text-xs text-gray-500">{view.date} {view.time}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Link Click Analytics */}
          {linkClickAnalytics && (
            <>
              {/* Contact Details Clicks */}
              <div>
                <h4 className="font-semibold text-[#252525] dark:text-white mb-3">Contact Details Clicks</h4>
                <div className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
                  <table className="w-full text-sm min-w-[400px]">
                    <thead className="bg-gray-50 dark:bg-[#252525]">
                      <tr>
                        <th className="text-left p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Link Type</th>
                        <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Today</th>
                        <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Last 7 Days</th>
                        <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Last 30 Days</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {linkClickAnalytics.contactDetails.map((item) => (
                        <tr key={item.type} className="hover:bg-gray-50 dark:hover:bg-[#252525]/50">
                          <td className="p-3 text-[#252525] dark:text-white whitespace-nowrap">
                            <span className="flex items-center gap-2">
                              {item.type === 'phone' && <Phone className="w-4 h-4 text-[#FFD12D]" />}
                              {item.type === 'whatsapp' && <MessageCircle className="w-4 h-4 text-green-500" />}
                              {item.type === 'email' && <Mail className="w-4 h-4 text-blue-500" />}
                              {item.type === 'website' && <Globe className="w-4 h-4 text-purple-500" />}
                              {item.label}
                            </span>
                          </td>
                          <td className="p-3 text-center text-green-600 font-semibold">{item.today || 0}</td>
                          <td className="p-3 text-center text-purple-600 font-semibold">{item.last7Days || 0}</td>
                          <td className="p-3 text-center text-yellow-600 font-semibold">{item.last30Days || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-1 sm:hidden">← Scroll to see more →</p>
              </div>

              {/* Social Media Clicks */}
              <div>
                <h4 className="font-semibold text-[#252525] dark:text-white mb-3">Social Media Clicks</h4>
                <div className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
                  <table className="w-full text-sm min-w-[400px]">
                    <thead className="bg-gray-50 dark:bg-[#252525]">
                      <tr>
                        <th className="text-left p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Platform</th>
                        <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Today</th>
                        <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Last 7 Days</th>
                        <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Last 30 Days</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {linkClickAnalytics.socialMedia.map((item) => (
                        <tr key={item.type} className="hover:bg-gray-50 dark:hover:bg-[#252525]/50">
                          <td className="p-3 text-[#252525] dark:text-white whitespace-nowrap">
                            {item.label}
                          </td>
                          <td className="p-3 text-center text-green-600 font-semibold">{item.today || 0}</td>
                          <td className="p-3 text-center text-purple-600 font-semibold">{item.last7Days || 0}</td>
                          <td className="p-3 text-center text-yellow-600 font-semibold">{item.last30Days || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-1 sm:hidden">← Scroll to see more →</p>
              </div>

              {/* Engagement Clicks (Share + Save Contact) */}
              {linkClickAnalytics.engagement && linkClickAnalytics.engagement.length > 0 && (
                <div>
                  <h4 className="font-semibold text-[#252525] dark:text-white mb-3">Engagement Clicks</h4>
                  <div className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
                    <table className="w-full text-sm min-w-[400px]">
                      <thead className="bg-gray-50 dark:bg-[#252525]">
                        <tr>
                          <th className="text-left p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Action</th>
                          <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Today</th>
                          <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Last 7 Days</th>
                          <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Last 30 Days</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {linkClickAnalytics.engagement.map((item) => (
                          <tr key={item.type} className="hover:bg-gray-50 dark:hover:bg-[#252525]/50">
                            <td className="p-3 text-[#252525] dark:text-white whitespace-nowrap">{item.label}</td>
                            <td className="p-3 text-center text-green-600 font-semibold">{item.today || 0}</td>
                            <td className="p-3 text-center text-purple-600 font-semibold">{item.last7Days || 0}</td>
                            <td className="p-3 text-center text-yellow-600 font-semibold">{item.last30Days || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 sm:hidden">← Scroll to see more →</p>
                </div>
              )}

              {/* Get Directions Clicks */}
              <div>
                <h4 className="font-semibold text-[#252525] dark:text-white mb-3">Get Directions Clicks</h4>
                <div className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
                  <table className="w-full text-sm min-w-[400px]">
                    <thead className="bg-gray-50 dark:bg-[#252525]">
                      <tr>
                        <th className="text-left p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Location</th>
                        <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Today</th>
                        <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Last 7 Days</th>
                        <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Last 30 Days</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {stall?.locations && stall.locations.length > 0 ? (
                        stall.locations.map((loc) => {
                          const locData = linkClickAnalytics.directionsClicks?.find(d => d.market === loc.market) || { today: 0, last7Days: 0, last30Days: 0 };
                          return (
                            <tr key={loc.market} className="hover:bg-gray-50 dark:hover:bg-[#252525]/50">
                              <td className="p-3 text-[#252525] dark:text-white whitespace-nowrap">
                                <span className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-[#FFD12D] flex-shrink-0" />
                                  {loc.market} {loc.stallNumber && `(#${loc.stallNumber})`}
                                </span>
                              </td>
                              <td className="p-3 text-center text-green-600 font-semibold">{locData.today || 0}</td>
                              <td className="p-3 text-center text-purple-600 font-semibold">{locData.last7Days || 0}</td>
                              <td className="p-3 text-center text-yellow-600 font-semibold">{locData.last30Days || 0}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr className="hover:bg-gray-50 dark:hover:bg-[#252525]/50">
                          <td className="p-3 text-[#252525] dark:text-white whitespace-nowrap">
                            <span className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-[#FFD12D] flex-shrink-0" />
                              All Locations
                            </span>
                          </td>
                          <td className="p-3 text-center text-green-600 font-semibold">{linkClickAnalytics.totalDirections?.today || 0}</td>
                          <td className="p-3 text-center text-purple-600 font-semibold">{linkClickAnalytics.totalDirections?.last7Days || 0}</td>
                          <td className="p-3 text-center text-yellow-600 font-semibold">{linkClickAnalytics.totalDirections?.last30Days || 0}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-1 sm:hidden">← Scroll to see more →</p>
              </div>
            </>
          )}

          <div className="bg-[#FFD12D]/10 border border-[#FFD12D] rounded-lg p-4">
            <p className="text-sm text-[#252525] dark:text-white">
              💡 <strong>Tip:</strong> Keep your stall information updated and add attractive images to increase views!
            </p>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="font-medium">No analytics available yet</p>
          <p className="text-sm mt-1">Analytics will appear once your stall is set up and receiving views.</p>
        </div>
      )}
    </div>
  );
};

export default VendorAnalyticsTab;
